<template>
  <div class="create-events" redesign>
    <!-- Request stepper -->
    <RequestStepProgressBar
      :isExpenseDocNeeded='currentRequest.requiresExpenseDocs'
      isCreateEvents
    />

    <!-- Event questions -->
    <div class="create-events-form form-container">
      <!-- Information -->
      <div v-if="content" class="form-header">
        <span class="tooltip-text" v-html="content" />
      </div>

      <!-- Display individual event questions -->
      <div class="event-container" v-for="event in events" :key="event.category">
        <EventComponent :event="event" @fieldBlur="saveFieldUpdate" />
      </div>

      <!-- Display request questions -->
      <div class="request-questions-form" v-if="currentRequest.requestQuestions.length">
        <h4 class="form-field-header" v-translate>Additional Questions</h4>
        <div v-bind:class="{ 'request-questions': currentRequest.requestQuestions.length }">
          <div
            v-for="question in currentRequest.requestQuestions"
            :key="question.id"
            class="form-field"
          >
            <Question v-if="!question.isText" class="question" :question="question" />
            <Question v-else class="question" :question="question" :isText="true" />
          </div>
        </div>
      </div>

      <!-- Default request questions -->
      <template v-if="!currentRequest.requestQuestions.length">
        <div class="request-questions-form">
          <h4 class="form-field-header" v-translate>Additional Questions</h4>
          <div class="form-field">
            <h5 class="form-field-header">
              *
              <translate
                >Please explain how the emergency (or emergencies) you selected created a financial
                hardship for you and your household? That is, how did they make it hard for you to
                afford basic living expenses?</translate
              >
            </h5>
            <div class="form-field-container">
              <textarea
                @input="mixin_TextAreaResize_resize"
                class="form-field-textarea"
                v-model="currentRequest.eventsFinancialHardshipAnswer"
                @blur="saveFieldUpdate"
              />
            </div>
          </div>
          <div class="form-field">
            <h5 class="form-field-header">
              *
              <translate
                >Was at least one of the emergencies described above unexpected or outside of your
                control?</translate
              >
            </h5>
            <div class="form-field-container">
              <select
                class="form-field-select"
                v-model="currentRequest.eventsUnexpectedOrUncontrollableMisc"
              >
                <option v-for="option in BOOLEAN_TEXT_CHOICES" :key="option.id" :value="option.id">
                  {{ option.text }}
                </option>
              </select>
              <span class="form-dropdown" />
            </div>
          </div>
          <div class="form-field">
            <h5 class="form-field-header">
              *
              <translate
                >Has the emergency or emergencies made it difficult for you to afford basic living
                expenses?</translate
              >
            </h5>
            <div class="form-field-container">
              <select
                class="form-field-select"
                v-model="currentRequest.eventsBasicLivingDifficultiesMisc"
                :style="{ width: '100%' }"
              >
                <option v-for="option in BOOLEAN_TEXT_CHOICES" :key="option.id" :value="option.id">
                  {{ option.text }}
                </option>
              </select>
              <span class="form-dropdown" />
            </div>
          </div>
          <div class="form-field">
            <h5 class="form-field-header" v-translate>
              Is there anything else you’d like to share with us about your emergency or
              emergencies?
            </h5>
            <div class="form-field-container">
              <textarea
                class="form-field-textarea"
                @input="mixin_TextAreaResize_resize"
                v-model="currentRequest.eventsMisc"
                @blur="saveFieldUpdate"
              />
            </div>
          </div>
        </div>
      </template>

      <!-- Error message -->
      <div
        v-if="hasSubmitted && !loading && (isEmptyEvent || requiredAnswersMissing)"
        class="error-container"
      >
        <div class="tooltip-icon">
          <img src="@/assets/icons/error-icon.png" />
        </div>
        <div class="tooltip">
          <span class="tooltip-text" v-translate>
            All questions are required to proceed except the final question. You can update your
            answers by editing the request later.
          </span>
        </div>
      </div>

      <!-- API error message(s) -->
      <div
        v-if="hasSubmitted && !loading && hasErrors && errors.length"
        class="error-container"
      >
        <div class="tooltip-icon">
          <img src="@/assets/icons/error-icon.png" />
        </div>
        <div class="tooltip">
          <div class="tooltip-text" v-html="errorMessages"></div>
        </div>
      </div>

      <!-- Next step -->
      <div class="create-events-form-footer form-footer">
        <div v-if="organizationDetails.eventDocumentationEnabled" class="next-step">
          <translate>Next step:</translate><br /><translate class="next-step-name" >{{organizationDetails.eventDetailDocumentation}}</translate>
        </div>
        <div v-else="organizationDetails.eventDocumentationEnabled" class="next-step">
          <translate>Next step:</translate><br /><translate class="next-step-name"
            >Describe your expenses</translate
          >
        </div>
      </div>

      <SubmitButton
        :loading="loading"
        :onContinue="onSubmit"
        actionName="commit-events"
        :staticWidth="true"
      />
    </div>
  </div>
</template>

<script>
import { Promise as bPromise } from 'bluebird'
import { mapState } from 'vuex'
import EventComponent from '@/components/requests/EventComponent'
import InfoBox from '@/components/InfoBox'
import EventCategory from '@/services/eventCategories'
import Event from '@/services/events/'
import Request from '@/services/requests'
import LoadSpinner from '@/components/LoadSpinner'
import EventQuestion from '@/services/eventQuestions'
import TextAreaResize from '../../mixins/TextAreaResize'
import { analyticsMixin } from '@/services/googleAnalytics'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'
import Question from '@/components/survey/Question'
import SubmitButton from '@/components/requests/SubmitButton'
import UserEvent from '@/services/userEvents'
import toMessageString from '@/services/api'

const currencyMask = createNumberMask({
  prefix: '$',
  allowDecimal: true,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  allowNegative: false,
})

export default {
  name: 'CreateEvents',
  mixins: [analyticsMixin, TextAreaResize],
  components: {
    EventComponent,
    LoadSpinner,
    InfoBox,
    RequestStepProgressBar,
    Question,
    SubmitButton,
  },
  data() {
    return {
      currencyMask,
      events: [],
      errors: [],
      hasErrors: false,
      loading: false,
      globalLoading: true,
      placeholder: this.$gettext('Answer'),
      BOOLEAN_TEXT_CHOICES: [
        {
          id: 'YES',
          text: this.$gettext('Yes'),
        },
        {
          id: 'NO',
          text: this.$gettext('No'),
        },
        {
          id: "I DON'T KNOW",
          text: this.$gettext("I don't know"),
        },
      ],
      hasSubmitted: false,
    }
  },
  async created() {
    this.globalLoading = true
    this.orderRequestQuestions()
    await this.assembleEvents()
    // this.startEditingPing()
    this.globalLoading = false
  },
  mounted() {
    // Update the current request completion status
    UserEvent.api.requestUpdateCompletionStatus(
      this.currentRequest.id,
      UserEvent.options.REQUEST_STEP_CREATE_EVENTS,
    )
  },
  destroyed() {
    clearInterval(this.interval)
  },
  methods: {
    getSelectionOptions(question) {
      if (question.friendlyName == 'Stimulus Check') {
        return this.BOOLEAN_TEXT_CHOICES
      }
      return this.BOOLEAN_TEXT_CHOICES.slice(0, 2)
    },
    async setSelected(value) {
      this.saveFieldUpdate()
    },
    async onSubmit() {
      this.errors = []
      this.loading = true
      this.hasSubmitted = true

      // If the event is empty or required answers are missing, stop.
      if (this.isEmptyEvent || this.requiredAnswersMissing) {
        this.loading = false
        return Promise.reject('missing-required-answer')
      }

      this.trackClickedCTA()
      let updateFields = []

      if (this.organizationDetails.enableWhenItHappenedEventQuestion) {
        updateFields = ['dateOccurred', 'questions']
      } else {
        updateFields = ['questions']
      }

      const promiseList = this.events.map(event => {
        // For each Event that has a populated ID, it already exists - update it
        if (event.id) {
          return Event.api.update(event, updateFields)
          // For each Event that does not, it does not yet exist - create it
        } else {
          return Event.api.create(event)
        }
      })
      // Wait for all Events to be created before updating Request
      await Promise.all(promiseList).catch((error) => {
        let {response} = error

        this.loading = false
        this.hasErrors = true
        this.errors.push(response)
      })

      if(this.hasErrors)
        return false

      updateFields = [
        'eventsFinancialHardshipAnswer',
        'eventsMisc',
        'eventsUnexpectedOrUncontrollableMisc',
        'eventsBasicLivingDifficultiesMisc',
        'requestQuestions',
      ]
      const response = await Request.api.update(this.currentRequest, updateFields)
      this.$store.dispatch('updateCurrentRequest', response)
      return response
    },
    async saveFieldUpdate() {
      let updateFields = []

      if (this.organizationDetails.enableWhenItHappenedEventQuestion) {
        updateFields = ['dateOccurred', 'questions']
      } else {
        updateFields = ['questions']
      }

      const promiseList = this.events.map(event => {
        // For each Event that has a populated ID, it already exists - update it
        if (event.id) {
          // Skip updating events that have incomplete fields
          let incompleteEvent = false
          event.questions.forEach(question => {
            if (!question.answer.text) {
              incompleteEvent = true
            }
          })
          if (incompleteEvent) {
            return
          } else {
            return Event.api.update(event, updateFields)
          }
        }
      })
      await Promise.all(promiseList)

      updateFields = [
        'eventsFinancialHardshipAnswer',
        'eventsMisc',
        'eventsUnexpectedOrUncontrollableMisc',
        'eventsBasicLivingDifficultiesMisc',
        'requestQuestions',
      ]

      const response = await Request.api.update(this.currentRequest, updateFields)
      this.$store.dispatch('updateCurrentRequest', this.currentRequest)
    },
    getErrorMessage(error) {
      let message = ''

      // Handle common error structures
      if (error.data.detail) {
        message += `<span>${error.data.detail}</span>`
      } else if (error.data.non_field_errors) {
        message += `<span>${error.data.non_field_errors}</span>`
      } else {
        message = toMessageString(error.data)
      }
      return message
    },
    orderRequestQuestions() {
      var last_item = this.currentRequest.requestQuestions.splice(
        this.currentRequest.requestQuestions.findIndex(question => question.order == -1),
        1,
      )
      this.currentRequest.requestQuestions.sort((first, second) => first.order - second.order)
      this.currentRequest.requestQuestions = this.currentRequest.requestQuestions.concat(last_item)

      // If multiple choice, add answer options for the Questions component.
      for (var index in this.currentRequest.requestQuestions) {
        var requestQuestion = this.currentRequest.requestQuestions[index]
        if (requestQuestion.qType == 'MULTIPLE_CHOICE' && !requestQuestion.options) {
          // Boolean & 'I don't know' answer options
          this.currentRequest.requestQuestions[index]['answerOptions'] =
            this.getSelectionOptions(requestQuestion)
          this.currentRequest.requestQuestions[index].isText = true
        } else if(requestQuestion.qType == 'MULTIPLE_CHOICE' && requestQuestion.options) {
          // Custom answer options
          let answerOptions = []
          for (let index in requestQuestion.options) {
            answerOptions.push({
              id: requestQuestion.options[index],
              text: requestQuestion.options[index],
            })
          }
          this.currentRequest.requestQuestions[index]['answerOptions'] = answerOptions
          this.currentRequest.requestQuestions[index].isText = true
        }
      }
    },
    /* Assemble an array of Event objects to populate this screen:
     *    - There will be an Event for each existing Event in the database.
     *    - There will be an new, empty Event for each new EventCategory
     *      selected on the previous screen.
     */
    async assembleEvents() {
      // Pull in any existing Events from state.
      // Cast them explicitly as Events, so we get access to their model methods.
      const events = this.currentRequest.eventsRef.map(e => Event.create(e))

      // Find out which of our EventCategories already have an associated Event,
      // and which ones don't yet
      const categoriesWithEvent = events.map(event => event.category)

      const categoriesWithoutEvent = []
      this.currentRequest.eventCategories.forEach(category => {
        if (!categoriesWithEvent.includes(category)) {
          categoriesWithoutEvent.push(category)
        }
      })
      // For each category that doesn't have an associated Event, pull the right info
      // from the API, and create a local Event object
      const categoryInfo = []
      await bPromise.each(categoriesWithoutEvent, async (category) => {
        categoryInfo.push(await EventCategory.api.retrieve(category))
      })

      categoryInfo.forEach(cat => {
        let event = Event.create({
          category: cat.id,
          categoryRef: cat,
          request: this.currentRequest.id,
          questions: cat.eventQuestions,
        })
        events.push(event)
      })
      // Render our combined list of new and existing Events
      this.events = events
    },
    startEditingPing() {
      const request = this.currentRequest
      Request.api.editingPing(request)
      this.interval = setInterval(function() {
        Request.api.editingPing(request)
      }, 10000)
    },
  },
  computed: {
    ...mapState(['currentRequest', 'organizationDetails']),
    content() {
      if (this.organizationDetails && this.organizationDetails.eventDetailDescription) {
          return this.organizationDetails.eventDetailDescription
      } else {
        this.$gettext('Please add details about each emergency.')
      }
    },
    disabled() {
      return this.globalLoading || this.loading || this.isEmptyEvent || this.requiredAnswersMissing
    },
    requiredAnswersMissing() {
      if (!this.currentRequest.requestQuestions.length) {
        return (
          !this.currentRequest.eventsFinancialHardshipAnswer ||
          !this.currentRequest.eventsUnexpectedOrUncontrollableMisc ||
          !this.currentRequest.eventsBasicLivingDifficultiesMisc
        )
      }

      return !this.currentRequest.requestQuestions.every(question => {
        if (question.isOptional) {
          return true
        }
        if (question.answer != null && question.answer.text != null && question.answer.text != '') {
          return true
        }
        return false
      })
    },
    legacyRequestQuestionsCheck() {
      return (
        (this.currentRequest &&
          this.currentRequest.eventsFinancialHardshipAnswer &&
          this.currentRequest.eventsFinancialHardshipAnswer != '') ||
        (this.currentRequest.eventsMisc && this.currentRequest.eventsMisc != '') ||
        (this.currentRequest.eventsUnexpectedOrUncontrollableMisc &&
          this.currentRequest.eventsUnexpectedOrUncontrollableMisc != '') ||
        (this.currentRequest.eventsBasicLivingDifficultiesMisc &&
          this.currentRequest.eventsBasicLivingDifficultiesMisc != '')
      )
    },
    /* Return true if one or more events are missing answers */
    isEmptyEvent() {
      let emptyEvent = false
      this.events.forEach(event => {
        // COVID19 is a special event that doesn't need answers and should be skipped
        if (!event.isCovid19()) {
          if (this.organizationDetails.enableWhenItHappenedEventQuestion && !event.dateOccurred) {
            emptyEvent = true
          }
          event.questions.forEach(q => {
            if (q.qType === EventQuestion.options.MULTIPLE_CHOICE) {
              if (!q.answer.option) {
                emptyEvent = true
              }
            } else {
              if (!q.answer.text) {
                emptyEvent = true
              }
            }
          })
        }
      })
      return emptyEvent
    },
    errorMessages() {
      let messages = ''
      this.errors.forEach(error => {
        messages += this.getErrorMessage(error)
      })
      return messages
    }
  },
  watch: {
    'currentRequest.eventsBasicLivingDifficultiesMisc': async function() {
      await this.saveFieldUpdate()
    },
    'currentRequest.eventsUnexpectedOrUncontrollableMisc': async function() {
      await this.saveFieldUpdate()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';

.loading-screen {
  margin: 3rem;
  height: 100vh;
}

.create-events {
  display: flex;
  flex-direction: column;
}

.create-events-form {
  margin-bottom: 1.5rem;

  @extend .constrain-tablet-and-up;
}

.request-questions-form {
  .form-container {
    margin-top: 1.5rem;
  }
}

#md-textarea {
  .form-field-textarea {
    background: white;
  }
}

.create-events-form-footer {
  margin-top: 2rem;
}
</style>
